import moment from "moment";
import { v4 as uuidv4 } from "uuid";

export const generateGuid = () => {
  const newGuid = uuidv4();
  return newGuid;
};

export const updateDocumentTitle = (title: string, success: boolean): void => {
  const defaultTitle = "THINK";
  const askQuestionTitle = `${defaultTitle} - ask a question`;

  if (success) {
    document.title = title.trim()
      ? `${defaultTitle} – ${title}`
      : askQuestionTitle;
  } else {
    document.title = defaultTitle;
  }
};

export const isStringEmpty = (str: string) => {
  return !str || str.trim().length === 0;
};

export const formatNumber = (number: any) => {
  return new Intl.NumberFormat("en-IN").format(number);
};

export const formatNumberPages = (number: any) => {
  return new Intl.NumberFormat("en-US").format(number);
};

export const formatConversationListDate = (dateString: string) => {
  return moment(dateString).format("MMM DD, YYYY");
};

export const setLocalStorageSearchIndexKey = (value: string | null) => {
  localStorage.setItem("SearchIndexKey", value || "");
};

export const setLocalStorageSearchIndexId = (value: string | null) => {
  localStorage.setItem("SearchIndexId", value || "");
};

export const clearLocalStorage = () => {
  localStorage.clear();
};

export const getExportDateTimeSuffix = (): string => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(now);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

  // ∕ = U+2215 DIVISION SLASH  (visually identical to /, valid in filenames)
  // ꞉ = U+A789 MODIFIER LETTER COLON  (visually identical to :, valid in filenames)
  const date = `${get("month")}∕${get("day")}∕${get("year")}`;
  const time = `${get("hour")}꞉${get("minute")} ${get("dayPeriod")}`;
  return `${date} ${time}`;
};

export const findGroupAndNameBySearchIndexId = (
  indexes: any,
  searchIndexId: string,
) => {
  for (const group of indexes) {
    const index = group.indexes.find(
      (index: any) => index.searchIndexId === searchIndexId,
    );
    if (index) {
      return { groupName: group.group, name: index.name, drafts: index.drafts };
    }
  }

  // If not found, return empty strings for both groupName and name
  return {
    groupName: "",
    name: "",
    drafts: [],
  };
};

export const getEndpoint = (
  conversationsLength: number,
  type: number,
  guid: string,
  useOnlyAttachedDocuments: boolean,
): string => {
  return conversationsLength === 0
    ? `${process.env.REACT_APP_THINK_LANGCHAIN_SERVICE_URL}/ask/v1/${guid}/${useOnlyAttachedDocuments}`
    : type === 2
      ? `${process.env.REACT_APP_THINK_LANGCHAIN_SERVICE_URL}/conversation/update/${guid}/draft`
      : `${process.env.REACT_APP_THINK_LANGCHAIN_SERVICE_URL}/ask/v1/${guid}/${useOnlyAttachedDocuments}`;
};

export const getSourceDocumentStreamEndpoint = (): string => {
  return `${process.env.REACT_APP_THINK_LANGCHAIN_SERVICE_URL}/chat/stream/source-documents`;
};

export const showDeepResearchButton = (
  selectedIndex: string | null,
): boolean => {
  return selectedIndex === "Audit" || selectedIndex === "Tax";
};

export const generateFileMetadata = (
  file: File,
  userName: string,
  sha1: string,
) => ({
  fileName: file.name,
  uploadedBy: userName || "Unknown User",
  uploadedAgo: new Date().toISOString(),
  fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
  type: file.type,
  sha1,
});

export const transformExternalFilesData = (files: any[]) => {
  const transformedList = files.map((file) => ({
    fileId: file.external_source_document_id,
    fileName: file.metadata?.fileName || file.external_source_document_name,
    uploadedBy: file.metadata?.uploadedBy || "Unknown User",
    uploadedAgo: file.metadata?.uploadedAgo || new Date().toISOString(),
    fileSize: file.metadata?.fileSize || "Unknown Size",
    type: file.metadata?.type || "Unknown Type",
    sha1: file.metadata?.sha1 || "Unknown SHA1",
    indexing_status: file.indexing_status || 0, // Default to 0 if not present
  }));

  return transformedList;
};

export const getFromNowDate = (date: string | Date) => {
  return moment(date).fromNow();
};

export const calculateSHA1 = async (file: File) => {
  // 1. Read the file into an ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();
  // 2. Compute the digest
  const digestBuffer = await crypto.subtle.digest("SHA-1", arrayBuffer);
  // 3. Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(digestBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return hashHex;
};

export const injectPageRefs = (md: any) => {
  const marker = "Relevant Pages:"; // exact marker from your example
  const markerIndex = md.indexOf(marker);

  // If marker not found, return original markdown unchanged
  if (markerIndex === -1) return md;

  // split the markdown into two parts: up to and including marker, and the rest
  const splitPoint = markerIndex + marker.length;
  const head = md.slice(0, splitPoint);
  const tail = md.slice(splitPoint);

  // Replace standalone numbers in the tail only and remove commas.
  // This keeps numbers elsewhere (TOC, code blocks, links, etc.) untouched.
  let replacedTail = tail.replace(
    /(^|[^\w[])(\d+)(?=($|[^\w]))/g,
    (_: string, beforeChar: string, num: string) => {
      return `${beforeChar}<page-ref data-page="${num}">${num}</page-ref>`;
    },
  );

  // Remove commas that appear between page-ref elements (with optional whitespace)
  replacedTail = replacedTail.replace(
    /(<\/page-ref>)\s*,\s*(?=<page-ref)/g,
    "$1 ",
  );

  return head + replacedTail;
};
